/* ── Shared types, constants & helpers for the PremiumFinanceScan (PremFi/PFA Scan) feature ── */
import type React from 'react';

export const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
export const getNcToken = () => localStorage.getItem('amsToken') || '';

/* ── Insurance company (drives which response mapper legacy code used —
 * both mappers are byte-identical, so a single shape is used here) ── */
export const INSURANCE_COMPANY_OPTIONS = [
  { value: 'CapitalPremiumFinancing', label: 'Capital Premium Financing' },
  { value: 'IPFS', label: 'IPFS' },
];

/* ── Types ── */
export interface NowCertsClient { name: string; databaseId: string; eMail?: string; insuredType?: number; }

export interface Policy {
  databaseId: string;
  policyNumber: string;
  carrierName?: string;
  linesOfBusiness?: string[];
  effectiveDate?: string;
  totalPremium?: number;
  clientName?: string;
  status?: string;
}

export interface PremFiInsuredData {
  insuredFirstName: string;
  policyNumber: string;
  premium: string;
  lineOfBusiness: string;
  term: string;
  carrierName: string;
  effectiveDate: string;
  quoteNumber: string;
  downPayment: string;
  financedAmount: string;
  effectiveAPR: string;
  otherTaxes: string;
  otherFees: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  submissionId: string;
  fileUrl: string;
}

/* ── 50-state list for the Address section's State dropdown ── */
export const US_STATES = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' }, { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }, { name: 'District of Columbia', code: 'DC' },
];

/* ── Formatting helpers ── */
export function stripMoney(v?: string | number | null): string {
  if (v === undefined || v === null || v === '') return '0';
  return String(v).replace(/\$/g, '').replace(/,/g, '');
}

export function parseAmount(val?: string | number | null): number {
  if (val === undefined || val === null || val === '') return 0;
  return parseFloat(String(val).replace(/[$,()]/g, '')) || 0;
}

export function toDateInput(val?: string): string {
  if (!val) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const mdy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  try {
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch { /* ignore */ }
  return '';
}

/* Native <option> popups ignore the page theme by default — setting background/color
 * directly on each <option> fixes it in Chromium/Firefox, but the color pair must
 * itself track the active theme or it looks wrong in the other mode. */
export const getOptionStyle = (theme: 'light' | 'dark'): React.CSSProperties =>
  theme === 'dark' ? { background: '#1a1829', color: '#e2e2f0' } : { background: '#ffffff', color: '#111827' };

/* Adds `termMonths` to `effectiveDate`, returned as MM/DD/YYYY — mirrors legacy createExpiryDate(). */
export function computeExpirationDate(effectiveDate: string, termMonths: string | number): string {
  const base = effectiveDate ? new Date(effectiveDate) : null;
  if (!base || isNaN(base.getTime())) return '';
  const months = parseInt(String(termMonths), 10) || 0;
  base.setMonth(base.getMonth() + months);
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${base.getFullYear()}`;
}

export function fmtDateSlash(d?: string): string {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${dt.getFullYear()}`;
}
